/**
 * Derived copy for `AssessmentEditorToolbar`. The toolbar owns no state — the page hook holds the
 * single `useValidateDefinition` instance and the save/duplicate calls; this only makes strings.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { AssessmentTemplateStatus } from './enums'

import { pluralize, withCount } from '@shared/lib/format'

import {
  getTemplateStatusColor,
  getTemplateStatusLabel,
} from './assessment-template-options'

/** Getters, not values — the toolbar re-renders on every store change and must read the current
 *  state, not a snapshot taken at setup. */
interface UseAssessmentEditorToolbarOptions {
  isDirty: () => boolean
  isSaving: () => boolean
  hasValidated: () => boolean
  hasProblems: () => boolean
  /** `null` when the count is not honestly available (a deep link — see the page hook). */
  attemptsCount: () => number | null
  /** `null` before a template is loaded. */
  status: () => AssessmentTemplateStatus | null
}

interface UseAssessmentEditorToolbarReturn {
  refDirtyText: ComputedRef<string>
  refSaveText: ComputedRef<string>
  /** The badge itself — "5 Assessments". Short, because it sits in the header row beside the
   *  buttons rather than on a line of its own. */
  refAttemptsLabel: ComputedRef<string | null>
  /** What the badge means, for its `title`: the count says there is something to be careful
   *  about, this says what. */
  refAttemptsHint: ComputedRef<string | null>
  refIsSummaryError: ComputedRef<boolean>
  /** The lifecycle badge — `null` while there is no status to show. */
  refStatusLabel: ComputedRef<string | null>
  refStatusColor: ComputedRef<AppBadgeColor>
}

export const useAssessmentEditorToolbar = (
  { isDirty, isSaving, hasValidated, hasProblems, attemptsCount, status }: UseAssessmentEditorToolbarOptions,
): UseAssessmentEditorToolbarReturn => {
  const refDirtyText = computed<string>(() =>
    isDirty() ? 'Unsaved changes' : 'All changes saved',
  )

  const refSaveText = computed<string>(() => (isSaving() ? 'Saving…' : 'Save'))

  /**
   * Only rendered when the count is known and non-zero. `null` means the admin deep-linked and the
   * list row is not in memory, where inventing a number would be a lie.
   */
  const refAttemptsCount = computed<number | null>(() => {
    const count = attemptsCount()
    return count === null || count <= 0 ? null : count
  })

  const refAttemptsLabel = computed<string | null>(() => {
    const count = refAttemptsCount.value
    return count === null ? null : withCount(count, { one: 'Assessment', other: 'Assessments' })
  })

  const refAttemptsHint = computed<string | null>(() => {
    const count = refAttemptsCount.value
    if (count === null) {
      return null
    }
    const subject = withCount(count, { one: 'assessment', other: 'assessments' })
    const verb = pluralize(count, { one: 'references', other: 'reference' })

    return `${subject} already ${verb} this template. Editing it changes what those attempts are graded against.`
  })

  const refIsSummaryError = computed<boolean>(() => hasValidated() && hasProblems())

  const refStatusLabel = computed<string | null>(() => {
    const value = status()
    return value ? getTemplateStatusLabel(value) : null
  })

  const refStatusColor = computed<AppBadgeColor>(() => {
    const value = status()
    return value ? getTemplateStatusColor(value) : 'slate'
  })

  return {
    refDirtyText,
    refSaveText,
    refAttemptsLabel,
    refAttemptsHint,
    refIsSummaryError,
    refStatusLabel,
    refStatusColor,
  }
}
